/**
 * The one canonical action path: how GSC enrichment joins a Radar audit.
 *
 * CLIENT-SAFE ON PURPOSE. The adapter that builds GSC actions lives in
 * gsc-actions.ts, which reads Supabase through the service-role client and
 * therefore cannot be imported into the browser bundle. The dashboard needs
 * the merge and the ownership rules, not the loader, so they live here and
 * this module imports no server code.
 *
 * It exists so the rules below have ONE definition instead of being written
 * once in the page and again in its tests — a copy the tests would then be
 * proving correct about itself.
 */

import type { ActionItem } from '@/types/radar'
import { sortActions } from './action-priority'
import { canonicalDomain } from './run-history'

/**
 * "No GSC actions for this audit." A shared constant so the identity is
 * stable: a fresh `[]` per render changes the merge memo's dependencies
 * every render and re-sorts the whole list continuously.
 */
export const NO_GSC_ACTIONS: ActionItem[] = []

/**
 * GSC actions together with the audit they were fetched for.
 *
 * Fetch identity and results ownership are part of the value, not metadata
 * kept beside it. That is the entire point — see deriveGscActions.
 */
export interface GscPayload {
  /** Normalized audited domain used to fetch these actions. */
  key: string
  /** Results instance that owns these actions. Autosave does not change it. */
  generation: number
  actions: ActionItem[]
}

export interface GscDerivation {
  /** Enrichment for the CURRENT audit has arrived. */
  settled: boolean
  /** Contributes to the merge. Empty unless the payload owns this audit. */
  actions: ActionItem[]
  /** Safe to serialize: enrichment finished, or there is nothing to enrich. */
  ready: boolean
}

/**
 * Decide what a held payload means for the audit currently on screen.
 *
 * WHY OWNERSHIP IS DERIVED, NOT GUARDED. Holding the actions in a bare
 * array with the audit key parked in a separate ref makes "which audit is
 * this for?" unanswerable from the state itself, so correctness falls to
 * effect ordering: clear-on-start, abort-on-change, a pending flag. Effects
 * run after paint, so there is always a committed render where the previous
 * audit's findings are merged into the new audit's action list — visible,
 * exportable, and sendable to the advisor. Auditing pixelmojo.io and then a
 * customer domain leaked internal findings into their report for that
 * window.
 *
 * With both identities inside the payload, data for another domain or an
 * older same-domain results instance simply is not the current audit's data.
 *
 * @param gscKey null when no audit is on screen — nothing to enrich.
 */
export function deriveGscActions(
  payload: GscPayload | null,
  gscKey: string | null,
  generation: number
): GscDerivation {
  const settled =
    payload !== null &&
    payload.key === gscKey &&
    payload.generation === generation
  return {
    settled,
    actions: settled ? payload.actions : NO_GSC_ACTIONS,
    ready: gscKey === null || settled,
  }
}

/**
 * Fetch/cache identity for GSC data. Null means "no completed audit".
 *
 * The normalized domain is the whole FETCH identity. Merge ownership stays
 * separate in GscPayload.generation, because two audits of the same domain
 * must never accept one another's late response.
 *
 * The run id used to be part of this, which meant auto-save changing
 * `live` to a saved id re-keyed a settled audit and fired a second
 * identical request for the same measured domain.
 *
 * It must be the AUDITED domain, never the editable input box: keying on
 * the box merged Pixelmojo's findings into an audit of avepoint.com and
 * fired one request per keystroke.
 */
export function gscAuditKey(
  auditedDomain: string | null | undefined,
  hasCompletedResults: boolean
): string | null {
  if (!hasCompletedResults || !auditedDomain) return null
  return canonicalDomain(auditedDomain) || null
}

/**
 * THE canonical action array. Every consumer reads the result of this —
 * panel, plan, dashboard, insights tab, roadmap, threads, both exports — so
 * a GSC action cannot reach one surface and miss another.
 *
 * Merged THEN sorted, never concatenated after a sort: appending to a
 * sorted array parks every GSC action at the end regardless of its tier.
 *
 * The restamp keeps `priority` honest. It documents itself as the 1-based
 * rank in canonical order, and generateActions stamps it across its OWN
 * array; merging GSC actions in reorders that array, so without restamping
 * every base action carries a pre-merge rank — the second, conflicting
 * ordering the field exists to prevent.
 *
 * New objects, never a mutation: GSC actions are held in React state, and
 * writing a rank through would persist one render's ordering into the next
 * audit's payload.
 */
export function mergeCanonicalActions(
  baseActions: ActionItem[],
  gscActions: ActionItem[]
): ActionItem[] {
  const merged = sortActions([...baseActions, ...gscActions])
  return merged.map((a, i) =>
    a.priority === i + 1 ? a : { ...a, priority: i + 1 }
  )
}
